// frontend/components/sidebar/RenameModal.tsx

"use client";

import React, { useState, useEffect, useRef } from "react";
import { FileText } from "lucide-react";
import * as Lucide from "lucide-react";
import IconPickerModal from "../doc/IconPickerModal";
import { getTwemojiSvgUrl } from "../../utils/emoji";

interface RenameModalProps {
  visible: boolean;
  title: string;
  x: number;
  y: number;
  icon: string | null;
  onTitleChange: (val: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  onIconChange: (icon: string | null) => void;
  documentId?: string;
}

export default function RenameModal({
  visible,
  title,
  x,
  y,
  icon,
  onTitleChange,
  onClose,
  onSubmit,
  onIconChange,
  documentId,
}: RenameModalProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [localIcon, setLocalIcon] = useState<string | null>(icon);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [posStyle, setPosStyle] = useState<{ top: number; left: number; opacity: number }>({ top: y, left: x, opacity: 0 });
  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    setLocalIcon(icon);
  }, [icon]);

  useEffect(() => {
    if (visible && modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      let newX = x;
      let newY = y;

      if (x + rect.width > winW) {
        newX = winW - rect.width - 10;
      }
      if (y + rect.height > winH) {
        newY = winH - rect.height - 10;
      }

      setPosStyle({ top: newY, left: newX, opacity: 1 });
    } else if (!visible) {
      setPosStyle(prev => ({ ...prev, opacity: 0 }));
      setIsPickerOpen(false);
    }
  }, [visible, x, y]);

  if (!visible) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSubmit();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const handleTogglePicker = () => {
    if (!isPickerOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const winH = window.innerHeight;
      const winW = window.innerWidth;
      const newStyle: React.CSSProperties = {
        position: "fixed",
        zIndex: 99999, // 최상위 강제
      };

      // 화면 하단 공간이 부족하면 버튼 위로, 아니면 버튼 아래로 강제 고정 (px 단위)
      if (winH - rect.bottom < 450) {
        newStyle.bottom = `${winH - rect.top + 8}px`;
      } else {
        newStyle.top = `${rect.bottom + 8}px`;
      }

      // 화면 우측 공간이 부족하면 버튼 왼쪽으로, 아니면 버튼 오른쪽으로 강제 고정 (px 단위)
      if (winW - rect.left < 350) {
        newStyle.right = `${winW - rect.right}px`;
      } else {
        newStyle.left = `${rect.left}px`;
      }

      setPickerStyle(newStyle);
    }
    setIsPickerOpen(!isPickerOpen);
  };

  const renderIcon = (iconValue: string | null) => {
    if (!iconValue) return <FileText size={14} />;

    if (iconValue.startsWith("emoji:")) {
      const code = iconValue.replace("emoji:", "");
      if (/^[0-9a-f-]+$/.test(code)) {
        return (
          <img
            src={getTwemojiSvgUrl(code)}
            alt="emoji"
            className="w-4 h-4 object-contain"
          />
        );
      }
      return <span className="text-xs">{code}</span>;
    }

    if (iconValue.startsWith("lucide:")) {
      const parts = iconValue.replace("lucide:", "").split(":");
      const iconName = parts[0];
      const colorHex = parts[1] || "currentColor";
      const IconComponent = (Lucide as any)[iconName];
      if (IconComponent) {
        return <IconComponent size={14} color={colorHex === "currentColor" ? undefined : colorHex} />;
      }
    }

    if (iconValue.startsWith("upload:") || iconValue.startsWith("data:")) {
      const src = iconValue.startsWith("upload:") ? iconValue.replace("upload:", "") : iconValue;
      return <img src={src} alt="icon" className="w-4 h-4 object-cover rounded-sm" />;
    }

    return <FileText size={14} />;
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-transparent"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />

      {/* 1. 이름 변경 메인 모달 */}
      <div
        ref={modalRef}
        className="fixed z-50 flex items-center space-x-1.5 bg-white dark:bg-zinc-900 p-1.5 rounded-md border border-gray-200 dark:border-zinc-800 shadow-xl transition-opacity duration-100"
        style={{ top: posStyle.top, left: posStyle.left, opacity: posStyle.opacity }}
      >
        <div className="relative flex items-center">
          <button
            ref={buttonRef}
            type="button"
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-zinc-800 border border-transparent hover:border-gray-200 dark:hover:border-zinc-700 text-gray-500 dark:text-zinc-400 shrink-0 transition-colors"
            onClick={handleTogglePicker}
          >
            {renderIcon(localIcon)}
          </button>
        </div>
        <input
          type="text"
          value={title}
          className="bg-transparent text-sm text-gray-900 dark:text-zinc-100 outline-none px-1 py-0.5 w-40"
          onChange={(e) => onTitleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </div>

      {/* 2. 아이콘 픽커를 RenameModal DOM 구조 바깥으로 완전히 빼내어 독립시킴 */}
      {isPickerOpen && (
        <div
          style={pickerStyle}
          className="[&>div]:relative! [&>div]:top-auto! [&>div]:bottom-auto! [&>div]:left-auto! [&>div]:right-auto! [&>div]:m-0! [&>div]:transform-none!"
          onClick={(e) => e.stopPropagation()}
        >
          <IconPickerModal
            documentId={documentId}
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            onSelect={(val) => {
              setLocalIcon(val);
              onIconChange(val);
              setIsPickerOpen(false);
            }}
          />
        </div>
      )}
    </>
  );
}